import React, { useCallback, useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Chip,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from '@material-ui/core';
import Alert from '@material-ui/lab/Alert';
import Skeleton from '@material-ui/lab/Skeleton';
import RefreshIcon from '@material-ui/icons/Refresh';

import SessionApi from '../../session-api';

const PAGE_SIZE = 10;
const EMPTY_FILTERS = {
  userId: '',
  source: '',
  from: '',
  to: '',
};
const SOURCE_CANONICAL_KEYS = {
  admin: 'web',
  api: 'web',
  calendar: 'google',
  email: 'system',
  google: 'google',
  'google-calendar': 'google',
  google_calendar: 'google',
  platform: 'web',
  scheduler: 'system',
  survey: 'web',
  system: 'system',
  web: 'web',
  whatsapp: 'whatsapp',
};
const SELECT_PROPS = {
  displayEmpty: true,
  MenuProps: {
    anchorOrigin: {
      vertical: 'bottom',
      horizontal: 'left',
    },
    getContentAnchorEl: null,
  },
};

const EVENT_TYPE_LABELS = {
  'attendance.accepted': 'Asistencia aceptada',
  'attendance.meet_reconciled': 'Asistencia consultada en Google Meet',
  'attendance.confirmed': 'Asistencia aceptada',
  'attendance.cancelled': 'Asistencia cancelada',
  'attendance.canceled': 'Asistencia cancelada',
  'attendance.declined': 'Asistencia rechazada',
  'attendance.rejected': 'Asistencia rechazada',
  'attendance.responded': 'Respuesta de asistencia registrada',
  'calendar.accepted': 'Asistencia aceptada en Google Calendar',
  'calendar.cancelled': 'Mentoría cancelada en Google Calendar',
  'calendar.canceled': 'Mentoría cancelada en Google Calendar',
  'calendar.declined': 'Asistencia rechazada en Google Calendar',
  'calendar.rejected': 'Asistencia rechazada en Google Calendar',
  'email.mentorship_instructions': 'Instructivo de mentoría enviado',
  'mentor_log.submitted': 'Bitácora del mentor completada',
  'notification.dispatch': 'Envío de notificación iniciado',
  'notification.failed': 'Envío de notificación fallido',
  'notification.sent': 'Notificación enviada',
  'restriction.applied': 'Restricción aplicada',
  'session.cancelled': 'Mentoría cancelada',
  'session.canceled': 'Mentoría cancelada',
  'survey.answered': 'Encuesta respondida',
  'survey.completed': 'Encuesta respondida',
  'survey.responded': 'Encuesta respondida',
  'whatsapp.accepted': 'Asistencia aceptada por WhatsApp',
  'whatsapp.confirmed': 'Asistencia aceptada por WhatsApp',
};

const SOURCE_LABELS = {
  google: 'Google',
  system: 'Sistema',
  web: 'Plataforma web',
  whatsapp: 'WhatsApp',
};

const STATUS_LABELS = {
  completed: 'Completado',
  failed: 'Fallido',
  processing: 'En proceso',
  unknown: 'Envío sin confirmar',
};

const ROLE_LABELS = {
  admin: 'Administrador',
  manager: 'Manager',
  mentee: 'Mentee',
  mentor: 'Mentor',
  none: 'none',
};

const NOTIFICATION_TYPE_LABELS = {
  attendance_confirmation: 'Recordatorio de confirmación enviado',
  calendar_cancellation: 'Aviso de cancelación por Calendar enviado',
  meeting_reminder: 'Recordatorio de reunión enviado',
  post_session_survey: 'Encuesta posterior enviada',
  restriction_applied_automatic:
    'Aviso de restricción automática enviado al usuario',
  restriction_applied_manual: 'Aviso de restricción manual enviado al usuario',
  restriction_cancelled:
    'Aviso de finalización manual de restricción enviado al usuario',
  restriction_expired: 'Aviso de expiración de restricción enviado al usuario',
  session_auto_cancelled: 'Aviso de cancelación automática enviado',
  session_reminder: 'Recordatorio de mentoría enviado',
};

const humanize = (value) => {
  if (!value) {
    return 'Sin especificar';
  }

  const text = String(value)
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .replace(/[._-]+/g, ' ')
    .trim()
    .toLowerCase();

  return text
    ? `${text.charAt(0).toUpperCase()}${text.slice(1)}`
    : 'Sin especificar';
};

const formatDate = (value) => {
  if (!value) {
    return 'Sin fecha';
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return 'Fecha inválida';
  }

  return date.toLocaleString('es-CL', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZone: 'America/Santiago',
  });
};

const getActivityDisplayStatus = (activity) => {
  const status = String(activity.status || 'unknown').toLowerCase();
  return status === 'failed' && activity.metadata?.deliveryStatus === 'unknown'
    ? 'unknown'
    : status;
};

const getActivityLabel = (activity) => {
  const eventType = String(activity.eventType || '').toLowerCase();
  const status = getActivityDisplayStatus(activity);
  const metadata = activity.metadata || {};
  const reminderHours = Number(
    metadata.reminderHours || metadata.reminder_hours || metadata.hoursBefore
  );

  if (eventType === 'email.attendance_confirmation') {
    const reminderLabel = metadata.reminderType === 'mentee_3h' || reminderHours === 3
      ? '3 horas para el emprendedor'
      : 'las 07:00';
    if (status === 'completed') {
      return `Correo de confirmación de ${reminderLabel} enviado`;
    }
    if (status === 'unknown') {
      return `Envío del correo de ${reminderLabel} sin confirmar`;
    }
    if (status === 'failed') {
      return `Falló el correo de confirmación de ${reminderLabel}`;
    }
    return `Procesando correo de confirmación de ${reminderLabel}`;
  }

  if (
    metadata.reminderType === 'same_day_7am' &&
    ['notification.dispatch', 'notification.sent', 'notification.failed'].includes(eventType)
  ) {
    if (eventType === 'notification.failed' || status === 'failed') {
      return 'Falló el recordatorio de las 07:00 del mismo día';
    }

    return eventType === 'notification.sent'
      ? 'Recordatorio de las 07:00 del mismo día enviado'
      : 'Procesando recordatorio de las 07:00 del mismo día';
  }

  if (eventType === 'notification.dispatch' && status === 'failed') {
    return Number.isFinite(reminderHours) && reminderHours > 0
      ? `Falló el recordatorio de ${reminderHours} h`
      : 'Envío de notificación fallido';
  }

  if (
    ['notification.dispatch', 'notification.sent'].includes(eventType) &&
    Number.isFinite(reminderHours) &&
    reminderHours > 0
  ) {
    return eventType === 'notification.sent'
      ? `Recordatorio de ${reminderHours} h enviado`
      : `Procesando recordatorio de ${reminderHours} h`;
  }

  if (
    eventType === 'notification.sent' &&
    NOTIFICATION_TYPE_LABELS[metadata.notificationType]
  ) {
    return NOTIFICATION_TYPE_LABELS[metadata.notificationType];
  }

  return EVENT_TYPE_LABELS[eventType] || humanize(activity.eventType);
};

const getSourceLabel = (source) => {
  const key = String(source || '').toLowerCase();
  return SOURCE_LABELS[key] || 'Sin especificar';
};

const canonicalizeSource = (source) => {
  const key = String(source || '').trim().toLowerCase();
  return SOURCE_CANONICAL_KEYS[key] || '';
};

const getStatusLabel = (status) => {
  const key = String(status || '').toLowerCase();
  return STATUS_LABELS[key] || 'Sin especificar';
};

const getRoleLabel = (role) => {
  const roleValue = typeof role === 'object' && role
    ? role.type || role.name
    : role;
  const key = String(roleValue || '').toLowerCase();
  return ROLE_LABELS[key] || humanize(roleValue);
};

const AutomationSummary = ({ participants, sessionId }) => {
  const requestIdRef = useRef(0);
  const [activities, setActivities] = useState([]);
  const [page, setPage] = useState(1);
  const [meta, setMeta] = useState({ total: 0, totalPages: 0 });
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [availableSources, setAvailableSources] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState('');
  const [reloadKey, setReloadKey] = useState(0);
  const hasActiveFilters = Object.values(filters).some(Boolean);
  const isDateRangeInvalid = Boolean(
    filters.from && filters.to && filters.from > filters.to
  );

  const fetchActivities = useCallback(async () => {
    const requestId = requestIdRef.current + 1;
    requestIdRef.current = requestId;
    setError('');

    if (isDateRangeInvalid) {
      setActivities([]);
      setMeta({ total: 0, totalPages: 0 });
      setIsLoading(false);
      return;
    }

    setIsLoading(true);

    try {
      const requestParams = {
        page,
        limit: PAGE_SIZE,
      };

      if (filters.userId) {
        requestParams.userId = Number(filters.userId);
      }
      if (filters.source) {
        requestParams.source = filters.source;
      }
      if (filters.from) {
        requestParams.from = filters.from;
      }
      if (filters.to) {
        requestParams.to = filters.to;
      }

      const response = await SessionApi.getAutomationSummary(
        sessionId,
        requestParams
      );

      if (requestId !== requestIdRef.current) {
        return;
      }

      const payload = response && response.data ? response.data : {};
      const responseData = Array.isArray(payload.data) ? payload.data : [];
      const responseMeta = payload.meta || {};
      const responseSources = Array.isArray(payload.filters?.sources)
        ? payload.filters.sources
        : [];
      const total = Math.max(0, Number(responseMeta.total) || 0);
      const totalPages = Math.max(
        0,
        Number(responseMeta.totalPages) || Math.ceil(total / PAGE_SIZE)
      );
      const safePage = Math.min(page, totalPages || 1);

      setActivities(responseData);
      setMeta({ total, totalPages });
      setAvailableSources(
        Array.from(
          new Set(responseSources.map(canonicalizeSource).filter(Boolean))
        ).sort((firstSource, secondSource) =>
          getSourceLabel(firstSource).localeCompare(
            getSourceLabel(secondSource),
            'es'
          )
        )
      );

      if (safePage !== page) {
        setPage(safePage);
      }
    } catch (requestError) {
      if (requestId !== requestIdRef.current) {
        return;
      }

      setActivities([]);
      setMeta({ total: 0, totalPages: 0 });
      setError(
        'No fue posible cargar el resumen de automatizaciones. Intenta nuevamente.'
      );
    } finally {
      if (requestId === requestIdRef.current) {
        setIsLoading(false);
      }
    }
  }, [filters, isDateRangeInvalid, page, sessionId]);

  useEffect(() => {
    fetchActivities();

    return () => {
      requestIdRef.current += 1;
    };
  }, [fetchActivities, reloadKey]);

  const handleFilterChange = ({ target: { name, value } }) => {
    setPage(1);
    setFilters((currentFilters) => ({
      ...currentFilters,
      [name]: value,
    }));
  };

  const handleClearFilters = () => {
    setPage(1);
    setFilters(EMPTY_FILTERS);
  };

  const renderActivityRow = (activity) => {
    const statusKey = getActivityDisplayStatus(activity);
    const user = activity.user || {};
    const confirmationDeadline = activity.metadata?.confirmationDeadline;
    const hasConfirmationDeadline = confirmationDeadline &&
      !Number.isNaN(new Date(confirmationDeadline).getTime());

    return (
      <TableRow hover key={activity.id}>
        <TableCell className="automation-summary__date-cell">
          <strong>{formatDate(activity.occurredAt)}</strong>
        </TableCell>
        <TableCell className="automation-summary__activity-cell">
          <strong>{getActivityLabel(activity)}</strong>
          {activity.description && (
            <span>{activity.description}</span>
          )}
          {hasConfirmationDeadline && (
            <span>
              Plazo de confirmación: {formatDate(confirmationDeadline)} (hora de Chile).
            </span>
          )}
        </TableCell>
        <TableCell>
          <Chip
            label={getSourceLabel(activity.source)}
            size="small"
            variant="outlined"
          />
        </TableCell>
        <TableCell>
          <Chip
            className={`automation-summary__status automation-summary__status--${statusKey}`}
            label={getStatusLabel(statusKey)}
            size="small"
          />
        </TableCell>
        <TableCell className="automation-summary__person-cell">
          <strong>{user.name || 'Sistema'}</strong>
          {user.role && <span>{getRoleLabel(user.role)}</span>}
        </TableCell>
      </TableRow>
    );
  };

  const paginationPage = meta.total > 0
    ? Math.min(page - 1, Math.max(0, meta.totalPages - 1))
    : 0;

  return (
    <section className="automation-summary" aria-labelledby="automation-summary-title">
      <div className="automation-summary__heading">
        <div>
          <h3 id="automation-summary-title">Resumen de automatizaciones</h3>
          <p>
            Revisa los envíos, respuestas y acciones automáticas asociadas a
            esta mentoría.
          </p>
        </div>
        <button
          className="session-meet-report__refresh"
          disabled={isLoading}
          onClick={() => setReloadKey((value) => value + 1)}
          type="button"
        >
          Actualizar
          <RefreshIcon aria-hidden="true" />
        </button>
      </div>

      <form
        aria-label="Filtrar automatizaciones"
        className="automation-summary__filters"
        onSubmit={(event) => event.preventDefault()}
        role="search"
      >
        <div className="automation-summary__filter automation-summary__filter--select">
          <span>Persona</span>
          <TextField
            id="automation-summary-person-filter"
            InputLabelProps={{ shrink: true }}
            label="Seleccionar persona"
            name="userId"
            onChange={handleFilterChange}
            SelectProps={SELECT_PROPS}
            select
            value={filters.userId}
          >
            <MenuItem value="">Todas las personas</MenuItem>
            {participants.map((participant) => {
              const role = String(
                participant.role?.type || participant.role?.name || ''
              ).toLowerCase();

              return (
                <MenuItem key={participant.id} value={String(participant.id)}>
                  {participant.name || 'Sin nombre'} ({role || 'sin rol'})
                </MenuItem>
              );
            })}
          </TextField>
        </div>

        <div className="automation-summary__filter automation-summary__filter--select">
          <span>Origen</span>
          <TextField
            id="automation-summary-source-filter"
            InputLabelProps={{ shrink: true }}
            label="Seleccionar origen"
            name="source"
            onChange={handleFilterChange}
            SelectProps={SELECT_PROPS}
            select
            value={filters.source}
          >
            <MenuItem value="">Todos los orígenes</MenuItem>
            {availableSources.map((source) => (
              <MenuItem key={source} value={source}>
                {getSourceLabel(source)}
              </MenuItem>
            ))}
          </TextField>
        </div>

        <label className="automation-summary__filter">
          <span>Desde</span>
          <input
            aria-describedby={
              isDateRangeInvalid ? 'automation-summary-date-error' : undefined
            }
            aria-invalid={isDateRangeInvalid}
            max={filters.to || undefined}
            name="from"
            onChange={handleFilterChange}
            type="date"
            value={filters.from}
          />
        </label>

        <label className="automation-summary__filter">
          <span>Hasta</span>
          <input
            aria-describedby={
              isDateRangeInvalid ? 'automation-summary-date-error' : undefined
            }
            aria-invalid={isDateRangeInvalid}
            min={filters.from || undefined}
            name="to"
            onChange={handleFilterChange}
            type="date"
            value={filters.to}
          />
        </label>

        <button
          className="automation-summary__clear"
          disabled={!hasActiveFilters}
          onClick={handleClearFilters}
          type="button"
        >
          Limpiar
        </button>

        {isDateRangeInvalid && (
          <p
            className="automation-summary__filter-error"
            id="automation-summary-date-error"
            role="alert"
          >
            La fecha desde no puede ser posterior a la fecha hasta.
          </p>
        )}
      </form>

      {error && (
        <Alert
          action={(
            <button
              className="automation-summary__retry"
              onClick={() => setReloadKey((value) => value + 1)}
              type="button"
            >
              Reintentar
            </button>
          )}
          className="automation-summary__error"
          severity="error"
        >
          {error}
        </Alert>
      )}

      <div aria-live="polite" className="automation-summary__results-summary">
        {isLoading
          ? 'Actualizando resultados…'
          : `${meta.total} ${meta.total === 1 ? 'actividad' : 'actividades'}`}
      </div>

      <TableContainer
        aria-busy={isLoading}
        className="automation-summary__table-container"
      >
        <Table aria-label="Resumen de automatizaciones de la mentoría" size="small">
          <TableHead>
            <TableRow>
              <TableCell>Fecha</TableCell>
              <TableCell>Actividad</TableCell>
              <TableCell>Origen</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Persona</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading
              ? Array.from({ length: 5 }).map((_, rowIndex) => (
                  <TableRow key={`automation-skeleton-${rowIndex}`}>
                    {Array.from({ length: 5 }).map((__, cellIndex) => (
                      <TableCell key={`automation-skeleton-${rowIndex}-${cellIndex}`}>
                        <Skeleton />
                      </TableCell>
                    ))}
                  </TableRow>
                ))
              : activities.map(renderActivityRow)}
            {!isLoading && activities.length === 0 && (
              <TableRow>
                <TableCell className="automation-summary__empty" colSpan={5}>
                  {error
                    ? 'No se pudieron obtener las automatizaciones.'
                    : isDateRangeInvalid
                      ? 'Corrige el rango de fechas para consultar las actividades.'
                      : hasActiveFilters
                        ? 'No hay actividades para los filtros seleccionados.'
                        : 'Aún no hay automatizaciones registradas para esta mentoría.'}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        component="div"
        count={meta.total}
        labelDisplayedRows={({ from, to, count }) => `${from}-${to} de ${count}`}
        labelRowsPerPage="Actividades por página:"
        onPageChange={(event, nextPage) => setPage(nextPage + 1)}
        page={paginationPage}
        rowsPerPage={PAGE_SIZE}
        rowsPerPageOptions={[PAGE_SIZE]}
      />
    </section>
  );
};

AutomationSummary.propTypes = {
  participants: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
      name: PropTypes.string,
      role: PropTypes.shape({
        name: PropTypes.string,
        type: PropTypes.string,
      }),
    })
  ),
  sessionId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
};

AutomationSummary.defaultProps = {
  participants: [],
};

export default AutomationSummary;
